/**
 * Select Components
 * Unified SimpleSelect and SearchableSelect components with consistent dropdown closing behavior
 */

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { theme } from '../../theme';
import { EntityLogo } from '../media/EntityLogo';

import { tokenVars } from '../../theme/tokens';
import { withAlpha } from '../../theme/colorUtils';
// Common types
export interface SelectOption {
  value: string;
  label: string;
  color?: string;
  logoUrl?: string;
  /** Optional badge text displayed as a small pill next to the label (e.g. active job count) */
  badge?: string;
}

export type SelectSize = 'xs' | 'small' | 'medium' | 'large';

// Logo size based on select size
const getLogoSize = (size: SelectSize): number => {
  switch (size) {
    case 'xs':
      return 16;
    case 'small':
      return 24;
    case 'medium':
      return 32;
    case 'large':
      return 48;
  }
};

// Size variants for selects - using theme system
const getSizeStyles = (size: SelectSize) => {
  const sizeConfig = theme.sizes.getSizeConfig(size);
  return {
    ...sizeConfig,
    fontSize: theme.typography.fontSize.md, // Consistent font size
  };
};

// Badge pill styles for active job counts
const badgeStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 20,
  height: 18,
  padding: '0 6px',
  borderRadius: 9,
  backgroundColor: tokenVars.brandFill,
  color: tokenVars.onAccent,
  fontSize: 11,
  fontWeight: 600,
  lineHeight: 1,
  flexShrink: 0,
};

// Common styles for both components - using theme
const selectStyles = {
  formGroup: {
    display: 'flex' as const,
    flexDirection: 'column' as const,
  },
  label: {
    display: 'block' as const,
    marginBottom: theme.spacing[1],
    fontSize: theme.typography.fontSize.sm,
    fontWeight: theme.typography.fontWeight.semibold,
    color: tokenVars.text.primary,
    fontFamily: theme.typography.fontFamily.primary,
    textAlign: 'left' as const,
  },
  // Base input styles - using theme system
  getInputStyles: (size: SelectSize) => ({
    width: '100%',
    backgroundColor: tokenVars.bgSubtle,
    border: `1px solid ${tokenVars.borderSubtle}`,
    borderRadius: theme.borders.input.radius,
    color: tokenVars.text.primary,
    fontFamily: theme.typography.fontFamily.primary,
    outline: 'none' as const,
    transition: `border-color ${theme.animations.duration.fast}ms ${theme.animations.easing.easeOut}`,
    ...getSizeStyles(size),
  }),
  dropdown: {
    position: 'absolute' as const,
    top: '100%',
    left: 0,
    right: 0,
    backgroundColor: tokenVars.surface,
    border: `1px solid ${tokenVars.border}`,
    borderRadius: theme.borders.radius.md,
    zIndex: 1000,
    maxHeight: 200,
    overflowY: 'auto' as const,
    boxShadow: theme.shadows.dropdown.default,
    marginTop: theme.spacing[1],
  },
  dropdownOption: {
    padding: theme.spacing[3],
    cursor: 'pointer',
    fontSize: theme.typography.fontSize.md,
    color: tokenVars.text.primary,
    backgroundColor: 'transparent',
    transition: `background-color ${theme.animations.duration.fast}ms ${theme.animations.easing.ease}`,
    border: 'none',
    width: '100%',
    textAlign: 'left' as const,
    fontFamily: theme.typography.fontFamily.primary,
  },
};

// SimpleSelect Props
interface SimpleSelectProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  size?: SelectSize;
  required?: boolean;
  usePortal?: boolean; // Render dropdown in a portal to avoid overflow clipping
}

// SimpleSelect Component
export const SimpleSelect: React.FC<SimpleSelectProps> = ({
  label,
  value,
  onChange,
  options,
  placeholder = 'Select...',
  disabled = false,
  size = 'medium',
  required = false,
  usePortal = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [dropdownDirection, setDropdownDirection] = useState<'down' | 'up'>('down');
  const [, forceUpdate] = useState({});
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find((option) => option.value === value);
  const logoSize = getLogoSize(size);

  const handleToggle = () => {
    if (!disabled) {
      if (!isOpen) {
        // Opening: Calculate dropdown direction before opening
        if (containerRef.current) {
          const rect = containerRef.current.getBoundingClientRect();

          // Find the closest scrollable parent (modal content area)
          let scrollableParent = containerRef.current.parentElement;
          while (scrollableParent) {
            const overflowY = window.getComputedStyle(scrollableParent).overflowY;
            if (overflowY === 'auto' || overflowY === 'scroll') {
              break;
            }
            scrollableParent = scrollableParent.parentElement;
          }

          // Calculate space relative to scrollable parent or viewport
          let spaceBelow: number;
          let spaceAbove: number;

          if (scrollableParent && !usePortal) {
            const parentRect = scrollableParent.getBoundingClientRect();
            spaceBelow = parentRect.bottom - rect.bottom;
            spaceAbove = rect.top - parentRect.top;
          } else {
            spaceBelow = window.innerHeight - rect.bottom;
            spaceAbove = rect.top;
          }

          const dropdownHeight = Math.min(200, options.length * 40); // Estimate dropdown height

          // Open upward if not enough space below but enough space above
          setDropdownDirection(spaceBelow < dropdownHeight && spaceAbove > dropdownHeight ? 'up' : 'down');
        }
        setIsOpen(true);
      } else {
        // Closing
        setIsOpen(false);
      }
    }
  };

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      // Check if click is outside both container and dropdown (for portal mode)
      if (
        containerRef.current &&
        !containerRef.current.contains(target) &&
        (!dropdownRef.current || !dropdownRef.current.contains(target))
      ) {
        setIsOpen(false);
      }
    };

    // Add listener on next tick to avoid immediate trigger
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside, true); // capture phase
      document.addEventListener('click', handleClickOutside, true);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside, true);
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [isOpen]);

  // Close dropdown on Escape key
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
      return () => document.removeEventListener('keydown', handleEscapeKey);
    }
  }, [isOpen]);

  // Force re-render on scroll/resize when using portal to update position
  useEffect(() => {
    if (!isOpen || !usePortal) return;

    const updatePosition = () => {
      forceUpdate({});
    };

    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen, usePortal]);

  return (
    <div style={label ? selectStyles.formGroup : {}}>
      {label && (
        <label style={selectStyles.label}>
          {label}
          {required && <span style={{ color: tokenVars.dangerText }}> *</span>}
        </label>
      )}
      <div ref={containerRef} style={{ position: 'relative' }}>
        <div style={{ position: 'relative' }}>
          <div
            onClick={handleToggle}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            tabIndex={disabled ? -1 : 0}
            style={{
              ...selectStyles.getInputStyles(size),
              cursor: disabled ? 'not-allowed' : 'pointer',
              color: selectedOption?.color || (selectedOption ? tokenVars.text.primary : tokenVars.text.muted),
              opacity: disabled ? 0.6 : 1,
              borderColor: isFocused ? tokenVars.focus : tokenVars.borderSubtle,
              boxShadow: isFocused ? `0 0 0 2px ${withAlpha(tokenVars.focus, 0.25)}` : 'none',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              paddingRight: 28,
            }}
          >
            {selectedOption?.logoUrl && <EntityLogo src={selectedOption.logoUrl} height={logoSize} />}
            <span
              style={{
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {selectedOption ? selectedOption.label : placeholder}
            </span>
            {selectedOption?.badge && <span style={badgeStyle}>{selectedOption.badge}</span>}
          </div>
          <span
            style={{
              position: 'absolute',
              right: theme.spacing[3],
              top: '50%',
              fontSize: theme.typography.fontSize.xs,
              color: tokenVars.text.muted,
              transform: isOpen ? 'translateY(-50%) rotate(180deg)' : 'translateY(-50%) rotate(0deg)',
              transition: `transform ${theme.animations.duration.fast}ms ${theme.animations.easing.ease}`,
              pointerEvents: 'none',
            }}
          >
            ▼
          </span>
        </div>

        {isOpen &&
          !disabled &&
          (() => {
            // Calculate dropdown styles based on portal and direction
            let dropdownStyles: React.CSSProperties = { ...selectStyles.dropdown };

            if (usePortal && containerRef.current) {
              // Portal mode: calculate position dynamically
              const rect = containerRef.current.getBoundingClientRect();
              dropdownStyles = {
                ...dropdownStyles,
                position: 'fixed',
                top: dropdownDirection === 'up' ? 'auto' : rect.bottom + theme.spacing[1],
                bottom: dropdownDirection === 'up' ? window.innerHeight - rect.top + theme.spacing[1] : 'auto',
                left: rect.left,
                right: 'auto',
                width: rect.width,
                marginTop: 0,
                marginBottom: 0,
                maxHeight:
                  dropdownDirection === 'up' ? rect.top - theme.spacing[1] : window.innerHeight - rect.bottom - theme.spacing[1] * 2,
                zIndex: 10003, // Higher than modal (10002)
              };
            } else if (dropdownDirection === 'up') {
              // Non-portal upward mode
              dropdownStyles = {
                ...dropdownStyles,
                bottom: '100%',
                top: 'auto',
                marginTop: 0,
                marginBottom: theme.spacing[1],
              };
            }

            const dropdownContent = (
              <div ref={dropdownRef} style={dropdownStyles}>
                {options.map((option, index) => (
                  <div
                    key={option.value}
                    onClick={() => handleSelect(option.value)}
                    style={{
                      ...selectStyles.dropdownOption,
                      borderBottom: index < options.length - 1 ? `1px solid ${tokenVars.borderSubtle}` : 'none',
                      backgroundColor: option.value === value ? tokenVars.surfaceRaised : 'transparent',
                      color: option.color || tokenVars.text.primary,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                    }}
                    onMouseOver={(e) => {
                      if (option.value !== value) {
                        e.currentTarget.style.backgroundColor = tokenVars.surfaceRaised;
                      }
                    }}
                    onMouseOut={(e) => {
                      if (option.value !== value) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    {option.logoUrl && <EntityLogo src={option.logoUrl} height={logoSize} />}
                    {option.color && !option.logoUrl && (
                      <div
                        style={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: option.color,
                          flexShrink: 0,
                        }}
                      />
                    )}
                    <span style={{ flex: 1 }}>{option.label}</span>
                    {option.badge && <span style={badgeStyle}>{option.badge}</span>}
                  </div>
                ))}
              </div>
            );

            return usePortal ? createPortal(dropdownContent, document.body) : dropdownContent;
          })()}
      </div>
    </div>
  );
};

// SearchableSelect Props
interface SearchableSelectProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  id?: string;
  size?: SelectSize;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  /** Optional style overrides applied to the trigger element */
  triggerStyle?: React.CSSProperties;
  /**
   * Called with the search text whenever it changes (including the reset to
   * '' on close/select). Lets the parent fetch matches server-side and feed
   * them back through `options` — for lists too large to load up front. The
   * parent owns debouncing. Options are still label-filtered locally.
   */
  onSearchChange?: (term: string) => void;
  /** Shows "Searching..." instead of "No options found" while a server search is in flight. */
  searchLoading?: boolean;
  /**
   * The options themselves are still loading. Shows "Loading..." instead of
   * "No options found" — so a picker whose list has not arrived yet stays
   * open and explains itself, rather than being disabled and inert.
   */
  optionsLoading?: boolean;
  /**
   * Extra text each option matches on, keyed by option value — an email, say.
   * The local filter narrows on label OR this, so a server search that matched
   * a field the label doesn't show (the backend matches email) isn't thrown
   * away by the client-side pass.
   */
  searchAliases?: Record<string, string>;
}

// SearchableSelect Component
export const SearchableSelect: React.FC<SearchableSelectProps> = ({
  label,
  value,
  onChange,
  options,
  placeholder = 'Select...',
  searchPlaceholder = 'Search...',
  size = 'medium',
  required = false,
  disabled = false,
  error = false,
  triggerStyle,
  onSearchChange,
  searchLoading = false,
  optionsLoading = false,
  searchAliases,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [dropdownDirection, setDropdownDirection] = useState<'down' | 'up'>('down');
  const [, forceUpdate] = useState({});
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Report search-text changes to the parent. Held in a ref so an inline
  // callback doesn't re-fire the effect on every parent render.
  const onSearchChangeRef = useRef(onSearchChange);
  onSearchChangeRef.current = onSearchChange;
  useEffect(() => {
    onSearchChangeRef.current?.(searchTerm);
  }, [searchTerm]);
  // Server-searchable lists always show the search box, however few options are loaded.
  const showSearch = options.length > 5 || !!onSearchChange;

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    const q = searchTerm.toLowerCase();
    return options.filter(
      (option) => option.label.toLowerCase().includes(q) || (searchAliases?.[option.value] || '').toLowerCase().includes(q)
    );
  }, [options, searchTerm, searchAliases]);

  const selectedOption = options.find((option) => option.value === value);
  const logoSize = getLogoSize(size);

  const handleToggle = () => {
    if (disabled) return;

    // Calculate dropdown direction before opening. The dropdown renders in a
    // fixed-position portal (below), so it escapes any scrollable/overflow
    // ancestor — measure against the viewport, not the modal body. This is what
    // keeps a long tag list from extending under the modal's sticky footer:
    // the panel is bounded to the available viewport space and scrolls itself.
    if (containerRef.current && !isOpen) {
      const rect = containerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const dropdownHeight = Math.min(300, options.length * 40 + (options.length > 5 ? 60 : 0)); // Account for search input

      // Open upward only if there isn't room below but there is above.
      setDropdownDirection(spaceBelow < dropdownHeight && spaceAbove > dropdownHeight ? 'up' : 'down');
    }

    setIsOpen(!isOpen);
    setSearchTerm('');
    setHighlightedIndex(-1);
  };

  const handleSelect = (optionValue: string) => {
    // Only call onChange if the value is actually different
    if (optionValue !== value) {
      onChange(optionValue);
    }
    setIsOpen(false);
    setSearchTerm('');
    setHighlightedIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case 'Escape':
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
        break;
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev < filteredOptions.length - 1 ? prev + 1 : 0));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : filteredOptions.length - 1));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
          handleSelect(filteredOptions[highlightedIndex].value);
        }
        break;
    }
  };

  // Close dropdown when clicking outside - same logic as SimpleSelect
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      // The dropdown renders in a portal (outside containerRef), so a click on
      // it must NOT be treated as "outside" — check both refs.
      if (
        containerRef.current &&
        !containerRef.current.contains(target) &&
        (!dropdownRef.current || !dropdownRef.current.contains(target))
      ) {
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
      }
    };

    // Add listener on next tick to avoid immediate trigger
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside, true); // capture phase
      document.addEventListener('click', handleClickOutside, true);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside, true);
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [isOpen]);

  // Reposition the fixed portal dropdown when the page/modal scrolls or resizes
  // so it stays pinned to the trigger while open.
  useEffect(() => {
    if (!isOpen) return;
    const updatePosition = () => forceUpdate({});
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen]);

  // Close dropdown on Escape key
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
      return () => document.removeEventListener('keydown', handleEscapeKey);
    }
  }, [isOpen]);

  // Focus search input when dropdown opens (only if search is shown)
  useEffect(() => {
    if (isOpen && showSearch && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen, showSearch]);

  return (
    <div style={label ? selectStyles.formGroup : {}}>
      {label && (
        <label style={selectStyles.label}>
          {label}
          {required && <span style={{ color: tokenVars.dangerText }}> *</span>}
        </label>
      )}
      <div ref={containerRef} style={{ position: 'relative' }}>
        <div style={{ position: 'relative' }}>
          <div
            onClick={handleToggle}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            onKeyDown={handleKeyDown}
            tabIndex={disabled ? -1 : 0}
            style={{
              ...selectStyles.getInputStyles(size),
              cursor: disabled ? 'not-allowed' : 'pointer',
              color: disabled ? tokenVars.text.disabled : selectedOption ? tokenVars.text.primary : tokenVars.text.muted,
              opacity: disabled ? 0.6 : 1,
              borderColor: error ? tokenVars.danger : isFocused ? tokenVars.focus : tokenVars.borderSubtle,
              borderWidth: error ? 2 : 1,
              boxShadow: error
                ? `0 0 0 1px ${withAlpha(tokenVars.danger, 0.25)}`
                : isFocused
                  ? `0 0 0 2px ${withAlpha(tokenVars.focus, 0.25)}`
                  : 'none',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              paddingRight: 28, // Extra space for arrow
              ...triggerStyle,
            }}
          >
            {selectedOption?.logoUrl && <EntityLogo src={selectedOption.logoUrl} height={logoSize} />}
            <span
              style={{
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {selectedOption ? selectedOption.label : placeholder}
            </span>
            {selectedOption?.badge && <span style={badgeStyle}>{selectedOption.badge}</span>}
          </div>
          <span
            style={{
              position: 'absolute',
              right: theme.spacing[3],
              top: '50%',
              transform: isOpen ? 'translateY(-50%) rotate(180deg)' : 'translateY(-50%) rotate(0deg)',
              transition: `transform ${theme.animations.duration.fast}ms ${theme.animations.easing.ease}`,
              pointerEvents: 'none',
              fontSize: theme.typography.fontSize.xs,
              color: disabled ? tokenVars.text.disabled : tokenVars.text.muted,
              opacity: disabled ? 0.6 : 1,
            }}
          >
            ▼
          </span>
        </div>

        {isOpen &&
          !disabled &&
          (() => {
            // Render in a fixed-position portal so the panel escapes the modal's
            // overflow:auto body and never extends under the sticky footer. Its
            // height is bounded to the available viewport space (and capped at
            // 300) so a long tag list scrolls inside the panel instead of
            // pushing the dialog frame.
            const rect = containerRef.current?.getBoundingClientRect();
            const gap = theme.spacing[1];
            const spaceBelow = rect ? window.innerHeight - rect.bottom - gap * 2 : 300;
            const spaceAbove = rect ? rect.top - gap * 2 : 300;
            const maxH = Math.max(120, Math.min(300, dropdownDirection === 'up' ? spaceAbove : spaceBelow));
            const dropdownStyles: React.CSSProperties = {
              ...selectStyles.dropdown,
              position: 'fixed',
              left: rect?.left,
              right: 'auto',
              width: rect?.width,
              top: dropdownDirection === 'up' ? 'auto' : (rect?.bottom ?? 0) + gap,
              bottom: dropdownDirection === 'up' ? window.innerHeight - (rect?.top ?? 0) + gap : 'auto',
              marginTop: 0,
              marginBottom: 0,
              maxHeight: maxH,
              zIndex: 10003, // above the modal (10002)
            };
            const dropdownContent = (
              <div ref={dropdownRef} style={dropdownStyles}>
                {/* Only show search if there are more than 5 options (or the parent searches server-side) */}
                {showSearch && (
                  <div style={{ padding: 8 }}>
                    <input
                      ref={searchInputRef}
                      type="text"
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setHighlightedIndex(-1);
                      }}
                      onFocus={() => setIsSearchFocused(true)}
                      onBlur={() => setIsSearchFocused(false)}
                      onKeyDown={handleKeyDown}
                      placeholder={searchPlaceholder}
                      style={{
                        ...selectStyles.getInputStyles(size),
                        marginBottom: 0,
                        fontSize: 12,
                        borderColor: isSearchFocused ? tokenVars.focus : tokenVars.borderSubtle,
                        boxShadow: isSearchFocused ? `0 0 0 2px ${withAlpha(tokenVars.focus, 0.25)}` : 'none',
                      }}
                    />
                  </div>
                )}

                {filteredOptions.length === 0 ? (
                  <div
                    style={{
                      padding: `${theme.spacing[2]}px ${theme.spacing[3]}px`,
                      color: tokenVars.text.muted,
                      fontSize: theme.typography.fontSize.sm,
                      textAlign: 'center',
                      fontFamily: theme.typography.fontFamily.primary,
                    }}
                  >
                    {searchLoading ? 'Searching...' : optionsLoading ? 'Loading...' : 'No options found'}
                  </div>
                ) : (
                  filteredOptions.map((option, index) => {
                    const isSelected = option.value === value;
                    const isHighlighted = index === highlightedIndex;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => handleSelect(option.value)}
                        style={{
                          ...selectStyles.dropdownOption,
                          backgroundColor: isHighlighted ? tokenVars.surfaceRaised : isSelected ? tokenVars.wash : 'transparent',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 12,
                          fontWeight: isSelected ? 600 : 400,
                          borderLeft: isSelected ? `3px solid ${tokenVars.brand}` : '3px solid transparent',
                        }}
                        onMouseEnter={() => setHighlightedIndex(index)}
                      >
                        {option.logoUrl && <EntityLogo src={option.logoUrl} height={logoSize} />}
                        <span style={{ flex: 1 }}>{option.label}</span>
                        {option.badge && <span style={badgeStyle}>{option.badge}</span>}
                      </button>
                    );
                  })
                )}
              </div>
            );
            return createPortal(dropdownContent, document.body);
          })()}
      </div>
    </div>
  );
};
